import { z } from "zod";
import {
  patientReflectionSchema,
  reflectionInputMethodSchema,
} from "./article";

// TES-1152 (Ö-566, Ö-567) — "Dagbok": the patient's private diary.
// GET /patient-api/v1/diary returns her OWN private notes and reflections,
// newest first, locale-resolved server-side.
//
// Emelie 2026-10-15 (Ö-566): Dagbok is the source of truth for her own notes
// and private reflections. Each one is a diary entry with a date and a
// source; Din resa may show that a reflection was made and link to it; the
// text is not copied and stored in several places; and it is not shown to
// the coach or the doctor just because it is in Dagbok.
//
// Nothing is copied to build it: an entry is read from the one row that
// already holds the words (PatientReflection for an article or course
// reflection, PrivateWeeklyReflection for the coachless F-04 note). The entry
// id is the same id Din resa's timeline gives that row
// (`reflection:<id>` / `weekly-reflection:<id>`), which is how a Din resa
// card links to its diary entry without the timeline contract changing.

// Where the entry came from. An open string on the wire so a source added
// later (Drive 2.1: "Egen anteckning", "Dosregistrering") does not break an
// older client; today's values:
//   weekly_checkin — her private F-04 note (and private answers) without a coach
//   dt_deepening   — a reflection on a DT deepening article
//   course_lesson  — a reflection on a course lesson
//   own_note       — TES-1406: a free note she wrote in Dagbok ("Skriv i
//                    Dagbok", source "Egen anteckning"); served only to a
//                    client claiming CLIENT_CONTRACT_CHANGES.diaryNotes
export const DIARY_SOURCES = [
  "weekly_checkin",
  "dt_deepening",
  "course_lesson",
  "own_note",
] as const;
export type DiarySource = (typeof DIARY_SOURCES)[number];

// One question and her answer to it. `question` is null when the source has
// no prompt to show (a retired article), never an internal key.
export const diaryEntryItemSchema = z.object({
  question: z.string().nullable(),
  answer: z.string(),
});
export type DiaryEntryItem = z.infer<typeof diaryEntryItemSchema>;

// TES-1406 (Ö-668 a) — a free note she wrote directly in Dagbok. Stored as a
// PatientReflection with sourceType "own_note" and no article (Drive Ella
// 2.1: "Samma PatientReflection-modell återanvänds"). Private: no staff
// reader, no AI, no medical signal. TES-1424 (Ö-692 a): she may share it with
// an active coach through POST /reflections/:id/share, which delivers a copy;
// the entry's `sharedWithCoach` then reads true.
export const diaryNoteSchema = z.object({
  // The PatientReflection id. The diary entry's id is `reflection:<id>`, the
  // same id Din resa's timeline gives the note.
  id: z.string(),
  // Drive's reflection_thread: the id of the first note in the thread.
  // "Reflektera igen" adds a note to it.
  threadId: z.string(),
  // 1 for the first note in the thread, then one per "Reflektera igen".
  sequence: z.number().int().positive(),
  title: z.string().nullable(),
  text: z.string(),
  inputMethod: reflectionInputMethodSchema,
  // Drive Ella 2.1, "Redigering, ny reflektion och radering": editable in
  // place until 24 hours after createdAt; after that the action is
  // "Reflektera igen". Computed server-side so the rule has one home.
  editable: z.boolean(),
  // ISO-8601: when `editable` turns false.
  editableUntil: z.string(),
  // TES-1534 (Ö-796) — on a shared note, true when she has saved a version
  // the coach does not have yet; Dagbok then offers "Dela med min coach"
  // again. Optional so an app reading an older server treats it as false.
  editedSinceShared: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DiaryNote = z.infer<typeof diaryNoteSchema>;

// Limits for an own note. Drive sets none; these are generous for a diary
// ("egna längre anteckningar") and keep a single row bounded.
export const DIARY_NOTE_TITLE_MAX = 120;
export const DIARY_NOTE_TEXT_MAX = 10000;

// Optional title: blank is no title.
const noteTitle = z
  .string()
  .max(DIARY_NOTE_TITLE_MAX)
  .nullish()
  .transform((v) => {
    const trimmed = v?.trim() ?? "";
    return trimmed.length > 0 ? trimmed : null;
  });

// Drive: "Anteckningstext krävs; en helt tom post får inte sparas." Text of
// only whitespace is empty.
const noteText = z
  .string()
  .max(DIARY_NOTE_TEXT_MAX)
  .refine((v) => v.trim().length > 0, { message: "text is required" });

// POST /patient-api/v1/diary/notes — "Spara i Dagbok".
export const diaryNoteCreateSchema = z.object({
  title: noteTitle,
  text: noteText,
  // Client-declared, as for reflections: dictation is the OS keyboard's and
  // no audio is ever sent or stored.
  inputMethod: reflectionInputMethodSchema.optional(),
  // A stable per-save uuid from the client: a retried request returns the
  // note it already created (Drive: "Återförsök får inte skapa dubbletter").
  clientKey: z.string().min(8).max(200),
  // "Reflektera igen": the id of a note in the thread to continue. Absent
  // for a new thread.
  continuesNoteId: z.string().min(1).max(200).optional(),
});
export type DiaryNoteCreate = z.infer<typeof diaryNoteCreateSchema>;

// PATCH /patient-api/v1/diary/notes/:id — an edit within the 24-hour window.
export const diaryNoteUpdateSchema = z.object({
  title: noteTitle,
  text: noteText,
  inputMethod: reflectionInputMethodSchema.optional(),
});
export type DiaryNoteUpdate = z.infer<typeof diaryNoteUpdateSchema>;

export const diaryEntrySchema = z.object({
  id: z.string(),
  source: z.string().min(1),
  // Patient-facing source label ("Veckans incheckning", the article or
  // lesson title); null when a retired source has no title any more.
  sourceTitle: z.string().nullable(),
  // ISO-8601. The same moment Din resa dates the entry by.
  occurredAt: z.string(),
  items: z.array(diaryEntryItemSchema),
  sharedWithCoach: z.boolean(),
  // Set for an article or course reflection: the row the app edits, removes
  // or shares through the existing /reflections endpoints. Null for a weekly
  // note, which is read-only, and for an own note, which carries `note`.
  reflection: patientReflectionSchema.nullable(),
  // TES-1406 — set only on an own note (source "own_note"): what the app
  // needs to edit, remove or "Reflektera igen" through /diary/notes. Absent
  // on every other entry.
  note: diaryNoteSchema.optional(),
});
export type DiaryEntry = z.infer<typeof diaryEntrySchema>;

export const diaryResponseSchema = z.object({
  // The persistent privacy notice (Drive Ella 2.1, "Integritet och
  // säkerhetscopy"), worded by her coach access — Ö-567 for a patient
  // without a coach.
  notice: z.object({ title: z.string(), body: z.string() }),
  entries: z.array(diaryEntrySchema),
});
export type DiaryResponse = z.infer<typeof diaryResponseSchema>;
